import { type ComponentProps, useCallback, useRef, useState } from "react";
import { Text } from "@zapier/design-system";

interface Props {
  value: string;
  type: ComponentProps<typeof Text>["type"];
  color?: ComponentProps<typeof Text>["color"];
  /** Single-line truncation class (overflow + ellipsis + nowrap). */
  className: string;
}

/**
 * Single-line text clamped with a CSS ellipsis; a native `title` shows the full
 * value on hover when it overflows. Truncation toggles only the `title` (never
 * the layout), so the measured node stays stable and the ellipsis can render.
 */
export function TruncatedText({ value, type, color, className }: Props) {
  const [isTruncated, setIsTruncated] = useState(false);
  const observerReference = useRef<ResizeObserver | undefined>(undefined);

  const measureNode = useCallback((node: HTMLDivElement | null) => {
    observerReference.current?.disconnect();
    if (!node) return;
    const measure = () =>
      setIsTruncated(node.scrollWidth > node.clientWidth + 1);
    measure();
    // Re-measure on later size changes — e.g. wiring a row grows its action
    // area and shrinks the app-name column, or the viewport crosses the modal's
    // breakpoint — so truncation stays correct, not just whatever fit at mount.
    observerReference.current = new ResizeObserver(measure);
    observerReference.current.observe(node);
  }, []);

  return (
    <div
      ref={measureNode}
      className={className}
      title={isTruncated ? value : undefined}
    >
      <Text type={type} color={color} tag="span">
        {value}
      </Text>
    </div>
  );
}
